import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ArrowLeft } from 'lucide-react';
import { Logo } from '../components/Logo';
import Footer from '../components/Footer';

/**
 * Terms of Service page organised into four role-based tabs so users can jump
 * straight to the section that applies to them. The copy is versioned here in
 * source so legal review remains reviewable via git; if you need to change it,
 * ship a code update.
 *
 * Structure per tab: an ordered list of `{ title, blocks[] }` where each block
 * is either a paragraph ({ p: '...' }) or a list ({ intro?: '...', items: [...] }).
 */
const TERMS_EFFECTIVE_DATE = 'April 2026';

const TERMS_DATA = {
    workers: {
        label: 'Workers',
        sections: [
            {
                title: 'Eligibility & Registration',
                blocks: [
                    { p: 'By creating an account, you confirm that you are at least 18 years old and legally eligible to work across international borders.' },
                    { p: 'Workers must provide accurate personal and professional information during registration.' },
                ],
            },
            {
                title: 'Worker Responsibilities',
                blocks: [
                    {
                        intro: 'Workers must:',
                        items: [
                            'Submit accurate documents.',
                            'Respond to job offers in good faith.',
                            'Comply with visa processing requirements.',
                        ],
                    },
                ],
            },
            {
                title: 'Document Submission',
                blocks: [
                    { p: 'Workers are responsible for ensuring that all uploaded documents, including passports, police/Interpol clearances, and other required documents, are genuine, current, and accurate.' },
                    { p: 'Submitting falsified documents will result in immediate account suspension and may lead to legal action.' },
                ],
            },
            {
                title: 'Offer Acceptance & Exclusivity',
                blocks: [
                    { p: 'Workers may accept only one job offer at a time.' },
                    {
                        intro: 'Accepting an offer automatically:',
                        items: [
                            'Cancels any pending interviews.',
                            'Rejects all other pending offers.',
                        ],
                    },
                    { p: 'If an accepted offer is later cancelled or rejected, the worker becomes eligible to receive future offers.' },
                ],
            },
            {
                title: 'Payment Terms',
                blocks: [
                    { p: 'Workers must pay a USD $500 document access fee upon visa approval to download visa and travel documents.' },
                    { p: 'Medical appointment fees are payable when appointments are booked. The fee amount is determined by the platform administrator for each medical partner.' },
                ],
            },
        ],
    },
    employers: {
        label: 'Employers',
        sections: [
            {
                title: 'Eligibility & Registration',
                blocks: [
                    { p: 'Employers must register using a valid company name and confirm they are legally eligible to hire workers across international borders.' },
                ],
            },
            {
                title: 'Employer Responsibilities',
                blocks: [
                    {
                        intro: 'Employers must:',
                        items: [
                            'Browse verified worker profiles.',
                            'Send legitimate employment offers.',
                            'Process visas for accepted candidates in a timely manner.',
                        ],
                    },
                ],
            },
            {
                title: 'Reimbursement for Candidate Withdrawal',
                blocks: [
                    { p: 'If a recruiter incurs documented visa or economy airfare expenses for a candidate matched through GulfBridge, and that candidate withdraws after accepting the job offer, GulfBridge will reimburse those expenses.' },
                ],
            },
            {
                title: 'Conditions',
                blocks: [
                    {
                        intro: 'Reimbursement is subject to the recruiter submitting through the GulfBridge platform:',
                        items: [
                            'Valid receipts or invoices for the expenses incurred.',
                            'The currency in which the expenses were paid.',
                        ],
                    },
                    { p: 'For the avoidance of doubt, reimbursement is limited to the direct cost of the visa and economy-class airfare and does not include any ancillary, administrative, processing, courier, transportation, service, agency, office, or other related expenses.' },
                    { p: 'GulfBridge will process reimbursement within 30 business days after receiving a complete submission.' },
                ],
            },
        ],
    },
    medical: {
        label: 'Medical Partners',
        sections: [
            {
                title: 'Medical Partner Responsibilities',
                blocks: [
                    {
                        intro: 'Medical partners must:',
                        items: [
                            'Provide accurate medical assessments.',
                            'Upload medical reports promptly following appointments.',
                        ],
                    },
                ],
            },
        ],
    },
    general: {
        label: 'General',
        sections: [
            {
                title: 'No Contact Sharing',
                blocks: [
                    { p: 'Workers and employers must not exchange personal contact information, including phone numbers or email addresses.' },
                    { p: 'All communication must occur through the GulfBridge platform.' },
                ],
            },
            {
                title: 'Document Verification Disclaimer',
                blocks: [
                    { p: 'GulfBridge is a technology platform that facilitates connections between employers and workers around the world seeking employment in the Gulf region.' },
                    { p: 'While GulfBridge supports document collection and profile verification processes, it does not independently verify the authenticity, accuracy, or legitimacy of documents, credentials, or information submitted by users.' },
                    { p: 'Workers are responsible for ensuring that all submitted information and documents are accurate, authentic, and up to date.' },
                    { p: 'Employers are responsible for conducting their own due diligence before making hiring decisions.' },
                    { p: 'GulfBridge accepts no liability for losses, disputes, damages, or other consequences resulting from reliance on documents or information that are later found to be inaccurate, fraudulent, or misleading.' },
                    { p: 'By using GulfBridge, all parties acknowledge and accept this limitation.' },
                ],
            },
            {
                title: 'Eligibility',
                blocks: [
                    { p: 'By creating an account, all users confirm they are at least 18 years old and legally eligible to participate in international recruitment in accordance with their role.' },
                ],
            },
            {
                title: 'Account Suspension',
                blocks: [
                    {
                        intro: 'GulfBridge reserves the right to suspend or permanently remove any account that:',
                        items: [
                            'Violates these Terms.',
                            'Submits fraudulent information.',
                            'Engages in abusive or inappropriate behaviour.',
                        ],
                    },
                ],
            },
            {
                title: 'Payment Processing',
                blocks: [
                    { p: 'All payments are processed through the GulfBridge platform.' },
                    { p: 'GulfBridge does not facilitate direct financial transactions between users.' },
                ],
            },
            {
                title: 'Platform Disclaimer',
                blocks: [
                    { p: 'GulfBridge is a hiring platform facilitator.' },
                    {
                        intro: 'GulfBridge is not:',
                        items: [
                            'A visa authority.',
                            'An employer of record.',
                            'An immigration agency.',
                        ],
                    },
                    { p: 'Visa approval decisions are made solely by the relevant government authorities.' },
                ],
            },
            {
                title: 'Governing Law',
                blocks: [
                    { p: 'These Terms are governed by the laws of Canada.' },
                    { p: 'Any disputes arising from these Terms shall be resolved through arbitration in Ontario, Canada.' },
                ],
            },
            {
                title: 'Changes to These Terms',
                blocks: [
                    { p: 'GulfBridge may update these Terms at any time.' },
                    { p: 'Continued use of the platform after changes become effective constitutes acceptance of the revised Terms.' },
                ],
            },
        ],
    },
};

const TAB_KEYS = ['workers', 'employers', 'medical', 'general'];

function Block({ block }) {
    if (block.items) {
        return (
            <>
                {block.intro && <p className="text-sm text-zinc-700 leading-relaxed mb-2">{block.intro}</p>}
                <ul className="list-disc pl-5 space-y-1 mb-3">
                    {block.items.map((item, i) => (
                        <li key={i} className="text-sm text-zinc-700 leading-relaxed">{item}</li>
                    ))}
                </ul>
            </>
        );
    }
    return <p className="text-sm text-zinc-700 leading-relaxed mb-3">{block.p}</p>;
}

export default function TermsPage() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('workers');

    useEffect(() => {
        const prev = document.title;
        document.title = 'Terms of Service — GulfBridge';
        return () => { document.title = prev; };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col" data-testid="terms-page">
            <header className="border-b border-zinc-200 bg-white sticky top-0 z-10">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 text-sm font-semibold"
                        data-testid="terms-back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/')}
                        className="hover:opacity-70 transition-opacity"
                        aria-label="GulfBridge home"
                        data-testid="terms-logo"
                    >
                        <Logo size="md" />
                    </button>
                </div>
            </header>

            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 w-full flex-1">
                <h1
                    className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight"
                    data-testid="terms-title"
                >
                    Terms of Service
                </h1>
                <p className="text-sm text-zinc-500 mt-3" data-testid="terms-effective-date">
                    <strong className="text-zinc-900">Effective Date:</strong> {TERMS_EFFECTIVE_DATE}
                </p>
                <p className="text-sm text-zinc-500 mt-2 max-w-2xl" data-testid="terms-subtitle">
                    These Terms apply to everyone using GulfBridge. Select your role below to jump straight
                    to what applies to you.
                </p>

                <div className="mt-6 sm:mt-8" data-testid="terms-tabs-wrapper">
                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                        <div className="overflow-x-auto -mx-4 sm:mx-0">
                            <TabsList
                                className="w-max sm:w-full sm:grid sm:grid-cols-4 rounded-none h-auto bg-transparent border-b border-zinc-200 p-0 mx-4 sm:mx-0 inline-flex"
                                data-testid="terms-tabs-list"
                            >
                                {TAB_KEYS.map(key => (
                                    <TabsTrigger
                                        key={key}
                                        value={key}
                                        className="rounded-none whitespace-nowrap border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-zinc-900 text-zinc-500 px-4 py-3 text-sm font-semibold"
                                        data-testid={`terms-tab-${key}`}
                                    >
                                        {TERMS_DATA[key].label}
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                        </div>

                        {TAB_KEYS.map(key => (
                            <TabsContent key={key} value={key} className="mt-6">
                                <div className="space-y-8" data-testid={`terms-content-${key}`}>
                                    {TERMS_DATA[key].sections.map((section, i) => (
                                        <section key={i} data-testid={`terms-section-${key}-${i}`}>
                                            <h2 className="font-heading text-lg sm:text-xl font-bold text-zinc-900 mb-3">
                                                {section.title}
                                            </h2>
                                            {section.blocks.map((block, j) => (
                                                <Block key={j} block={block} />
                                            ))}
                                        </section>
                                    ))}
                                </div>
                            </TabsContent>
                        ))}
                    </Tabs>
                </div>

                <div className="mt-12 pt-6 border-t border-zinc-100 text-xs text-zinc-500" data-testid="terms-footnote">
                    For questions about these Terms, please use the{' '}
                    <a href="/contact" className="text-zinc-900 underline font-semibold hover:opacity-70" data-testid="terms-contact-link">
                        Contact form
                    </a>{' '}
                    available on the platform.
                </div>
            </div>

            <Footer />
        </div>
    );
}
